import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { StepTypeSchema } from './step.schema.js';
// Extend Zod with OpenAPI
extendZodWithOpenApi(z);
// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');
// ─── Request Schemas ────────────────────────────────────────────────────────
// Generate personal lesson input
export const GeneratePersonalLessonSchema = z.object({
    prompt: z
        .string()
        .min(3, 'Prompt must be at least 3 characters')
        .max(1000, 'Prompt must be less than 1000 characters')
        .trim()
        .openapi({
        description: 'User prompt describing what they want to learn',
        example: 'Teach me about photosynthesis in plants',
    }),
});
// List personal lessons query parameters
export const ListPersonalLessonsQuerySchema = z.object({
    page: z
        .string()
        .optional()
        .default('1')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0, 'Page must be greater than 0'),
    pageSize: z
        .string()
        .optional()
        .default('10')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0 && val <= 100, 'Page size must be between 1 and 100'),
    search: z
        .string()
        .max(255, 'Search query must be less than 255 characters')
        .trim()
        .optional()
        .openapi({
        description: 'Search personal lessons by title or prompt',
        example: 'photosynthesis',
    }),
    folderId: z
        .string()
        .optional()
        .openapi({
        description: 'Filter by folder ID. Use "unfiled" for lessons without a folder.',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    sortBy: z
        .enum(['createdAt', 'title'])
        .optional()
        .default('createdAt')
        .openapi({
        description: 'Field to sort by',
        example: 'createdAt',
    }),
    sortOrder: z
        .enum(['asc', 'desc'])
        .optional()
        .default('desc')
        .openapi({
        description: 'Sort direction',
        example: 'desc',
    }),
});
// Personal lesson ID parameter validation
export const PersonalLessonIdParamSchema = z.object({
    lessonId: uuidSchema.openapi({
        description: 'Unique identifier of the personal lesson',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// ─── Response Schemas ───────────────────────────────────────────────────────
// Personal lesson response
export const PersonalLessonSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Unique personal lesson identifier',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    lessonId: uuidSchema.openapi({
        description: 'ID of the underlying lesson record',
        example: '660e8400-e29b-41d4-a716-446655440000',
    }),
    title: z.string().openapi({
        description: 'Generated lesson title',
        example: 'Introduction to Photosynthesis',
    }),
    prompt: z.string().openapi({
        description: 'Original user prompt',
        example: 'Teach me about photosynthesis in plants',
    }),
    fileName: z.string().nullable().openapi({
        description: 'Name of uploaded file used as context, if any',
        example: 'biology-notes.pdf',
    }),
    learnStepCount: z.number().int().openapi({
        description: 'Number of learning steps in the lesson',
        example: 5,
    }),
    reviseStepCount: z.number().int().openapi({
        description: 'Number of revision steps in the lesson',
        example: 3,
    }),
    estimatedMs: z.number().nullable().openapi({
        description: 'Estimated lesson duration in milliseconds based on user analytics or defaults',
        example: 480000,
    }),
    questionTypes: z.array(StepTypeSchema).openapi({
        description: 'Step types used in this lesson',
        example: ['mcq', 'text', 'true_false'],
    }),
    folderId: uuidSchema.nullable().openapi({
        description: 'ID of the folder this lesson belongs to, or null if unfiled',
    }),
    folderName: z.string().nullable().openapi({
        description: 'Name of the folder this lesson belongs to, or null if unfiled',
    }),
    createdAt: z.string().openapi({
        description: 'Timestamp when the personal lesson was created',
    }),
})
    .openapi('PersonalLesson');
// Revisable personal lesson (for revision selection)
export const RevisablePersonalLessonSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Unique personal lesson identifier',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    title: z.string().openapi({
        description: 'Lesson title',
        example: 'Introduction to Photosynthesis',
    }),
    reviseStepCount: z.number().int().openapi({
        description: 'Number of revision steps available',
        example: 3,
    }),
    questionTypes: z.array(StepTypeSchema).openapi({
        description: 'Step types available for revision',
        example: ['mcq', 'true_false'],
    }),
})
    .openapi('RevisablePersonalLesson');
// ─── Folder Schemas ─────────────────────────────────────────────────────────
// Create folder input
export const CreateFolderSchema = z.object({
    name: z
        .string()
        .min(1, 'Folder name is required')
        .max(100, 'Folder name must be less than 100 characters')
        .trim()
        .refine((n) => n.toLowerCase() !== 'unfiled', '"Unfiled" is a reserved folder name')
        .openapi({
        description: 'Name of the folder',
        example: 'Physics',
    }),
    color: z
        .string()
        .max(20)
        .nullable()
        .optional()
        .openapi({
        description: 'Hex color for folder icon',
        example: '#8b5cf6',
    }),
});
// Rename folder input
export const RenameFolderSchema = z.object({
    name: z
        .string()
        .min(1, 'Folder name is required')
        .max(100, 'Folder name must be less than 100 characters')
        .trim()
        .refine((n) => n.toLowerCase() !== 'unfiled', '"Unfiled" is a reserved folder name')
        .openapi({
        description: 'New name for the folder',
        example: 'Advanced Physics',
    }),
    color: z
        .string()
        .max(20)
        .nullable()
        .optional()
        .openapi({
        description: 'Hex color for folder icon',
        example: '#8b5cf6',
    }),
});
// Folder ID parameter
export const FolderIdParamSchema = z.object({
    folderId: uuidSchema.openapi({
        description: 'Unique folder identifier',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Move lesson to folder input
export const MoveLessonToFolderSchema = z.object({
    folderId: uuidSchema.nullable().openapi({
        description: 'Target folder ID, or null to move to unfiled',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Folder response
export const PersonalLessonFolderSchema = z
    .object({
    id: uuidSchema,
    name: z.string(),
    color: z.string().nullable(),
    position: z.number().int(),
    lessonCount: z.number().int(),
    createdAt: z.string(),
})
    .openapi('PersonalLessonFolder');
//# sourceMappingURL=personal-lesson.schema.js.map